import api from './api';
import { AuditStatus, AuditType, CweEntry, Risikoszenario, Rolle, Schwachstellenklasse, SchwachstellenStatus, Status, Tool } from '../types';

export interface SchwachstellenklasseTranslationResult {
  translated_text: string;
}

// Generic CRUD for lookup tables
function createLookupService<T extends { id: number }>(endpoint: string) {
  const collectionEndpoint = endpoint.endsWith('/') ? endpoint : `${endpoint}/`;
  return {
    list: async (): Promise<T[]> => {
      const response = await api.get(collectionEndpoint);
      return response.data;
    },
    create: async (data: Omit<T, 'id'>): Promise<T> => {
      const response = await api.post(collectionEndpoint, data);
      return response.data;
    },
    update: async (id: number, data: Partial<T>): Promise<T> => {
      const response = await api.put(`${collectionEndpoint}${id}`, data);
      return response.data;
    },
    delete: async (id: number): Promise<void> => {
      await api.delete(`${collectionEndpoint}${id}`);
    },
  };
}

// Rollen sind systemseitig fest definiert – nur Lesen erlaubt
export const rollenService = {
  list: async (): Promise<Rolle[]> => {
    const response = await api.get('/rollen/');
    return response.data;
  },
};
export const auditTypesService = createLookupService<AuditType>('/audit-types');
export const schwachstellenklassenService = {
  ...createLookupService<Schwachstellenklasse>('/schwachstellenklassen'),
  translate: async (
    text: string,
    sourceLanguage: 'de' | 'en',
    targetLanguage: 'de' | 'en'
  ): Promise<SchwachstellenklasseTranslationResult> => {
    const response = await api.post('/schwachstellenklassen/translate', {
      text,
      source_language: sourceLanguage,
      target_language: targetLanguage,
    });
    return response.data;
  },
};

export const cweService = {
  search: async (q?: string, limit: number = 50): Promise<CweEntry[]> => {
    const params: Record<string, string | number> = { limit };
    if (q) params.q = q;
    const response = await api.get('/cwe/', { params });
    return response.data;
  },
  get: async (id: number): Promise<CweEntry> => {
    const response = await api.get(`/cwe/${id}`);
    return response.data;
  },
};
export const toolsService = createLookupService<Tool>('/tools');
export const risikoszenarienService = {
  ...createLookupService<Risikoszenario>('/risikoszenarien'),
  translate: async (
    text: string,
    sourceLanguage: 'de' | 'en',
    targetLanguage: 'de' | 'en'
  ): Promise<SchwachstellenklasseTranslationResult> => {
    const response = await api.post('/risikoszenarien/translate', {
      text,
      source_language: sourceLanguage,
      target_language: targetLanguage,
    });
    return response.data;
  },
};

// Status service with reorder support
export const statusService = {
  ...createLookupService<Status>('/status'),
  reorder: async (orderedIds: number[]): Promise<Status[]> => {
    const response = await api.put('/status/reorder', { ordered_ids: orderedIds });
    return response.data;
  },
};

// AuditStatus service with reorder support
export const auditStatusService = {
  ...createLookupService<AuditStatus>('/audit-status'),
  reorder: async (orderedIds: number[]): Promise<AuditStatus[]> => {
    const response = await api.put('/audit-status/reorder', { ordered_ids: orderedIds });
    return response.data;
  },
};

// SchwachstellenStatus service with reorder support
export const schwachstellenStatusService = {
  ...createLookupService<SchwachstellenStatus>('/schwachstellen-status'),
  reorder: async (orderedIds: number[]): Promise<SchwachstellenStatus[]> => {
    const response = await api.put('/schwachstellen-status/reorder', { ordered_ids: orderedIds });
    return response.data;
  },
};
